import { useEffect, useState } from "react";
import ComponentCard from "../common/ComponentCard";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Select from "../form/Select";
import MultiSelectCheckbox from '../form/MultiSelectCheckbox';
import { Monitor, Smartphone } from "lucide-react";
import Switch from "../form/switch/Switch";
import Button from "../ui/button/Button";
import { useNavigate } from "react-router";
import React from "react";
import axios from "axios";
import { authService } from "../../services/authService";

// Funções utilitárias para cores (versão simplificada)
const hexToRgb = (hex: string): { r: number, g: number, b: number } => {
    hex = hex.replace('#', '');
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return { r, g, b };
};

const rgbToHex = ({ r, g, b }: { r: number, g: number, b: number }): string => {
    const toHex = (c: number) => {
        const hex = Math.max(0, Math.min(255, c)).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Versões simplificadas das funções de conversão HSV
const rgbToHsv = ({ r, g, b }: { r: number, g: number, b: number }): { h: number, s: number, v: number } => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s, v = max;
    const d = max - min;
    s = max === 0 ? 0 : d / max;
    if (max !== min) {
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    return { h: h * 360, s, v };
};

const hsvToRgb = ({ h, s, v }: { h: number, s: number, v: number }): { r: number, g: number, b: number } => {
    h /= 360;
    let r, g, b;
    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);
    switch (i % 6) {
        case 0: r = v; g = t; b = p; break;
        case 1: r = q; g = v; b = p; break;
        case 2: r = p; g = v; b = t; break;
        case 3: r = p; g = q; b = v; break;
        case 4: r = t; g = p; b = v; break;
        case 5: r = v; g = p; b = q; break;
        default: r = 0; g = 0; b = 0;
    }
    return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255)
    };
};

interface UpsellFormData {
    name: string;
    acceptButtonText: string;
    refuseButtonText: string;
    acceptRedirectUrl: string;
    refuseRedirectUrl: string;
    upsellRedirectUrl: string;
    active: boolean;
    global: boolean;
    purchasingId: string;
    purchaseVariantId: string;
    offerId: string; // ID do produto que será oferecido
    offerVariantId: string; // ID da oferta específica
    offerName?: string; // Nome da oferta (para exibição)
    offerProductName?: string; // Nome do produto da oferta (para exibição)
}

interface EditUpsellFormProps {
    productId?: string;
    upsellId?: string;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
}

// Componente de Preview
interface UpsellPreviewProps {
    formValues: UpsellFormData;
    primaryColor: string;
    colorText: string;
}

const UpsellPreview: React.FC<UpsellPreviewProps> = ({
    formValues,
    primaryColor,
    colorText
}) => {
    const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

    // Valores para exibir no preview
    const acceptButtonText = formValues.acceptButtonText || 'Sim, eu aceito essa oferta especial!';
    const refuseButtonText = formValues.refuseButtonText || 'Não, eu gostaria de recusar essa oferta';

    return (
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03] p-4 sticky top-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Preview do Upsell</h3>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setPreviewMode('desktop')}
                        className="p-2 rounded-md bg-gray-100 dark:bg-gray-800"
                        style={previewMode === 'desktop' ? { backgroundColor: primaryColor, color: 'white' } : {}}
                    >
                        <Monitor size={16} />
                    </button>
                    <button
                        type="button"
                        onClick={() => setPreviewMode('mobile')}
                        className="p-2 rounded-md bg-gray-100 dark:bg-gray-800"
                        style={previewMode === 'mobile' ? { backgroundColor: primaryColor, color: 'white' } : {}}
                    >
                        <Smartphone size={16} />
                    </button>
                </div>
            </div>

            {/* Card principal */}
            <div
                className={`p-4 rounded-lg transition-all duration-300 border border-gray-200 dark:border-gray-700 w-full ${previewMode === 'mobile' ? 'mx-auto' : ''}`}
                style={{
                    maxWidth: previewMode === 'mobile' ? '320px' : '100%',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
            >
                <div className="flex flex-col items-center justify-center py-6">
                    {/* Botão de aceite */}
                    <button
                        type="button"
                        className="px-8 py-3 rounded-md text-white font-medium transition-all duration-200 hover:opacity-90 mb-4 w-full sm:w-auto text-center"
                        style={{ backgroundColor: primaryColor }}
                    >
                        {acceptButtonText}
                    </button>

                    {/* Texto de recusa */}
                    <a
                        href="#"
                        className="text-center no-underline hover:underline transition-colors duration-200"
                        style={{ color: colorText }}
                    >
                        {refuseButtonText}
                    </a>
                </div>
            </div>
        </div>
    );
};

// Componente de seletor de cores
interface ColorPickerProps {
    color: string;
    onChange: (color: string) => void;
    label: string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ color, onChange, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [rgbValues, setRgbValues] = useState({ r: 0, g: 0, b: 0 });
    const [hue, setHue] = useState(0);

    // Inicializar valores RGB a partir da cor hex quando o componente é montado ou a cor muda
    useEffect(() => {
        try {
            const rgb = hexToRgb(color);
            setRgbValues(rgb);
            const hsv = rgbToHsv(rgb);
            setHue(hsv.h);
        } catch (e) {
            console.error("Erro ao converter cor:", e);
        }
    }, [color]);

    const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        onChange(inputValue);

        // Tentativa de validar e corrigir a cor
        let newColor = inputValue;

        // Adicione # se não tiver e for um valor hexadecimal válido
        if (!newColor.startsWith('#') && /^([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(newColor)) {
            newColor = '#' + newColor;
        }

        // Verifique se é uma cor hexadecimal válida
        if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(newColor)) {
            // Atualizar valores RGB
            try {
                const rgb = hexToRgb(newColor);
                setRgbValues(rgb);
                // Atualizar valor de matiz
                const hsv = rgbToHsv(rgb);
                setHue(hsv.h);
            } catch (e) {
                console.error("Erro ao converter cor:", e);
            }
        }
    };

    const handleColorBlur = () => {
        if (!/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
            let cleanColor = color.replace(/[^A-Fa-f0-9]/g, '');

            if (cleanColor.length === 3 || cleanColor.length === 6) {
                cleanColor = '#' + cleanColor;
                onChange(cleanColor);

                try {
                    const rgb = hexToRgb(cleanColor);
                    setRgbValues(rgb);
                    const hsv = rgbToHsv(rgb);
                    setHue(hsv.h);
                } catch (e) {
                    onChange('#FF3E1C');
                    setRgbValues({ r: 255, g: 62, b: 28 });
                }
            } else {
                onChange('#FF3E1C');
                setRgbValues({ r: 255, g: 62, b: 28 });
            }
        }
    };

    const handleGradientClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

        const hsv = { h: hue, s: x, v: 1 - y };
        const rgb = hsvToRgb(hsv);

        setRgbValues(rgb);
        onChange(rgbToHex(rgb));
    };

    const handleColorSelectorDrag = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.buttons !== 1) return;
        handleGradientClick(e);
    };

    const handleHueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newHue = Number(e.target.value);
        setHue(newHue);

        const hsv = rgbToHsv(rgbValues);
        const newRgb = hsvToRgb({ h: newHue, s: hsv.s, v: hsv.v });

        setRgbValues(newRgb);
        onChange(rgbToHex(newRgb));
    };

    const handleRgbChange = (component: 'r' | 'g' | 'b') => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value);
        if (isNaN(value)) return;

        // Limitar valores entre 0 e 255
        const validValue = Math.max(0, Math.min(255, value));
        const newRgbValues = { ...rgbValues, [component]: validValue };
        setRgbValues(newRgbValues);

        // Atualizar cor hexadecimal
        const newColor = rgbToHex(newRgbValues);
        onChange(newColor);

        // Atualizar o matiz
        const hsv = rgbToHsv(newRgbValues);
        setHue(hsv.h);
    };

    return (
        <div className="relative">
            <Label>{label}</Label>
            <div className="flex items-center gap-2">
                <div
                    className="w-8 h-8 rounded border border-gray-300 dark:border-gray-700 cursor-pointer"
                    style={{ backgroundColor: color }}
                    onClick={() => setIsOpen(!isOpen)}
                />
                <Input
                    type="text"
                    value={color}
                    onChange={handleColorChange}
                    onBlur={handleColorBlur}
                />
            </div>

            {isOpen && (
                <div className="absolute z-10 mt-2 p-4 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 w-64">
                    {/* Gradiente de cor */}
                    <div
                        className="w-full h-32 rounded-lg mb-4 relative cursor-crosshair"
                        style={{
                            background: `linear-gradient(to bottom, rgba(0,0,0,0) 0%, #000 100%),
                                        linear-gradient(to right, #fff 0%, hsl(${hue}, 100%, 50%) 100%)`
                        }}
                        onClick={handleGradientClick}
                        onMouseMove={handleColorSelectorDrag}
                    >
                        {/* Seletor de cor */}
                        <div
                            className="absolute w-4 h-4 rounded-full border-2 border-white shadow-md transform -translate-x-1/2 -translate-y-1/2"
                            style={{
                                backgroundColor: color,
                                top: `${100 - (rgbToHsv(rgbValues).v * 100)}%`,
                                left: `${rgbToHsv(rgbValues).s * 100}%`
                            }}
                        />
                    </div>

                    {/* Slider de matiz */}
                    <div className="mb-4">
                        <input
                            type="range"
                            min="0"
                            max="360"
                            value={hue}
                            onChange={handleHueChange}
                            className="w-full h-4 rounded-lg appearance-none cursor-pointer"
                            style={{
                                background: `linear-gradient(to right, 
                                    #f00 0%, #ff0 17%, #0f0 33%, 
                                    #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)`
                            }}
                        />
                    </div>

                    {/* Inputs RGB */}
                    <div className="grid grid-cols-3 gap-2">
                        <div>
                            <Label htmlFor="r-input" className="text-xs">R</Label>
                            <Input
                                id="r-input"
                                type="number"
                                min="0"
                                max="255"
                                value={rgbValues.r}
                                onChange={handleRgbChange('r')}
                            />
                        </div>
                        <div>
                            <Label htmlFor="g-input" className="text-xs">G</Label>
                            <Input
                                id="g-input"
                                type="number"
                                min="0"
                                max="255"
                                value={rgbValues.g}
                                onChange={handleRgbChange('g')}
                            />
                        </div>
                        <div>
                            <Label htmlFor="b-input" className="text-xs">B</Label>
                            <Input
                                id="b-input"
                                type="number"
                                min="0"
                                max="255"
                                value={rgbValues.b}
                                onChange={handleRgbChange('b')}
                            />
                        </div>
                    </div>

                    <div className="mt-4 flex justify-end">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setIsOpen(false)}
                        >
                            Fechar
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default function EditUpsellForm({ productId, upsellId, onSuccess, onError }: EditUpsellFormProps) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<UpsellFormData>({
        name: "",
        acceptButtonText: "Sim, quero adicionar esse produto ao meu pedido",
        refuseButtonText: "Não, eu não quero essa oferta",
        acceptRedirectUrl: "",
        refuseRedirectUrl: "",
        upsellRedirectUrl: "",
        active: true,
        global: false,
        purchasingId: "",
        purchaseVariantId: "",
        offerId: "",
        offerVariantId: "",
        offerName: "",
        offerProductName: ""
    });

    const [products, setProducts] = useState<{ value: string, label: string }[]>([]);
    const [productOffers, setProductOffers] = useState<{ value: string, label: string }[]>([]);
    const [upsellProductOffers, setUpsellProductOffers] = useState<{ value: string, label: string }[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [loadingOffers, setLoadingOffers] = useState(false);
    const [loadingUpsellOffers, setLoadingUpsellOffers] = useState(false);
    const [acceptButtonColor, setAcceptButtonColor] = useState("#ff4646");
    const [refuseButtonColor, setRefuseButtonColor] = useState("#929292");
    const [selectAllOffers, setSelectAllOffers] = useState(false);
    const [selectedOffers, setSelectedOffers] = useState<string[]>([]);

    // Variáveis para armazenar os dados originais do upsell
    const [originalScriptContent, setOriginalScriptContent] = useState<string>('');
    const [originalTriggerId, setOriginalTriggerId] = useState<string>('');

    // Carregar dados do upsell existente
    useEffect(() => {
        if (upsellId) {
            const fetchUpsellData = async () => {
                try {
                    setLoading(true);
                    const response = await axios.get(
                        `${import.meta.env.VITE_API_URL}/api/upsells/${upsellId}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${authService.getToken()}`
                            }
                        }
                    );

                    if (response.data && response.data.success) {
                        const upsellData = response.data.data;
                        setOriginalScriptContent(upsellData.scriptContent || '');
                        setOriginalTriggerId(upsellData.triggerId || '');

                        // Configurar cores dos botões
                        setAcceptButtonColor(upsellData.acceptButtonColor || "#ff4646");
                        setRefuseButtonColor(upsellData.refuseButtonColor || "#929292");

                        // Configurar state de ofertas selecionadas
                        if (upsellData.settingsData) {
                            if (upsellData.settingsData.global) {
                                setSelectAllOffers(true);
                                setSelectedOffers(['all']);
                            } else if (upsellData.settingsData.offerList && upsellData.settingsData.offerList.length > 0) {
                                setSelectedOffers(upsellData.settingsData.offerList);
                            }
                        }

                        // Extrair informações do produto e da oferta
                        let offerName = '';
                        let offerProductId = '';
                        let offerProductName = '';

                        // Extrair nome da oferta e ID do produto da oferta
                        if (upsellData.offer) {
                            offerName = upsellData.offer.name || '';
                            offerProductId = upsellData.offer.productId || '';

                            // Formatar o preço para exibição no nome da oferta
                            const formattedPrice = upsellData.offer.price ?
                                `R$ ${parseFloat(upsellData.offer.price).toFixed(2).replace('.', ',')}` :
                                'Preço não disponível';

                            // Adicionar o preço ao nome da oferta
                            if (offerName && formattedPrice) {
                                offerName = `${offerName} - ${formattedPrice}`;
                            }
                        }

                        // Configurar o formData com os dados existentes
                        setFormData({
                            name: upsellData.name || "",
                            acceptButtonText: upsellData.acceptButtonText || "",
                            refuseButtonText: upsellData.refuseButtonText || "",
                            acceptRedirectUrl: upsellData.acceptRedirectUrl || "",
                            refuseRedirectUrl: upsellData.refuseRedirectUrl || "",
                            upsellRedirectUrl: upsellData.upsellRedirectUrl || "",
                            active: upsellData.settingsData?.active ?? true,
                            global: upsellData.settingsData?.global ?? false,
                            purchasingId: "",
                            purchaseVariantId: "",
                            offerId: upsellData.productOfferId || "",
                            offerVariantId: upsellData.settingsData?.offerVariantId || upsellData.offerId || "",
                            offerName: offerName,
                            offerProductName: offerProductName
                        });

                        // Carregar produtos e buscar o nome do produto da oferta
                        await fetchProducts();

                        // Se temos o ID do produto da oferta, vamos buscar seu nome
                        if (offerProductId) {
                            try {
                                const productResponse = await axios.get(
                                    `${import.meta.env.VITE_API_URL}/api/products/${offerProductId}`,
                                    {
                                        headers: {
                                            'Authorization': `Bearer ${authService.getToken()}`
                                        }
                                    }
                                );

                                if (productResponse.data && productResponse.data.product) {
                                    const productData = productResponse.data.product;
                                    offerProductName = productData.name || '';

                                    // Atualizar o formData com o nome do produto da oferta
                                    setFormData(prev => ({
                                        ...prev,
                                        offerProductName: offerProductName
                                    }));
                                }
                            } catch (error) {
                                console.error("Erro ao buscar produto da oferta:", error);
                            }
                        }

                        // Carregar ofertas do produto atual para o MultiSelectCheckbox
                        fetchProductOffers();
                    }
                } catch (error) {
                    console.error("Erro ao carregar dados do upsell:", error);
                    setApiError("Erro ao carregar dados do upsell");
                    if (onError) onError(error);
                } finally {
                    setLoading(false);
                }
            };

            fetchUpsellData();
        }
    }, [upsellId]);

    // Carregar produtos ativos ao carregar o componente
    useEffect(() => {
        fetchProducts();
        fetchProductOffers(); // Carrega as ofertas do produto atual
    }, []);

    // Função para buscar produtos ativos
    const fetchProducts = async () => {
        try {
            setLoadingProducts(true);
            const response = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/products?status=active`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // O backend retorna { products: [...] }
            if (response.data && response.data.products) {
                const filteredProducts = response.data.products
                    .filter((product: any) => product.id !== productId) // Remove o produto atual
                    .map((product: any) => ({
                        value: product.id,
                        label: product.name
                    }));
                setProducts(filteredProducts);
            }
        } catch (error) {
            console.error("Erro ao carregar produtos:", error);
        } finally {
            setLoadingProducts(false);
        }
    };

    // Função para buscar ofertas de um produto específico
    const fetchProductOffers = async () => {
        try {
            setLoadingOffers(true);
            const response = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/products/${productId}/offers-with-links`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // Verificar o formato da resposta do backend
            if (response.data && response.data.offers) {
                // Criar opções para o dropdown com as ofertas do produto
                const offerOptions = response.data.offers.map((offer: any) => {
                    // Formatar o preço para exibição
                    const formattedPrice = offer.price ?
                        `R$ ${parseFloat(offer.price).toFixed(2).replace('.', ',')}` :
                        'Preço não disponível';

                    return {
                        value: offer.id,
                        label: `${offer.name} - ${formattedPrice}`
                    };
                });

                // Adicionar a opção "Qualquer oferta" no início da lista
                const allOptions = [
                    { value: 'any', label: ' Qualquer oferta' },
                    ...offerOptions
                ];

                setProductOffers(allOptions);
            }
        } catch (error) {
            console.error("Erro ao carregar ofertas do produto:", error);
        } finally {
            setLoadingOffers(false);
        }
    };

    // Função para buscar ofertas de um produto específico que será oferecido no upsell
    const fetchUpsellProductOffers = async (productId: string) => {
        if (!productId) return;

        try {
            setLoadingUpsellOffers(true);
            const response = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/products/${productId}/offers-with-links`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            if (response.data && response.data.offers) {
                const offerOptions = response.data.offers.map((offer: any) => {
                    const formattedPrice = offer.price ?
                        `R$ ${parseFloat(offer.price).toFixed(2).replace('.', ',')}` :
                        'Preço não disponível';

                    return {
                        value: offer.id,
                        label: `${offer.name} - ${formattedPrice}`
                    };
                });

                setUpsellProductOffers(offerOptions);
            }
        } catch (error) {
            console.error("Erro ao carregar ofertas do produto de upsell:", error);
        } finally {
            setLoadingUpsellOffers(false);
        }
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { id, value } = e.target;

        // Limpar o erro específico quando o campo é modificado
        if (errors[id]) {
            setErrors({
                ...errors,
                [id]: ""
            });
        }

        // Limpar mensagens de API
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");

        // Atualizar o estado
        setFormData({ ...formData, [id]: value });
    };

    const handleOfferProductChange = (value: string) => {
        setFormData(prev => ({
            ...prev,
            offerId: value,
            offerVariantId: '' // Reset the selected offer variant when changing product
        }));

        // Buscar ofertas do produto selecionado
        fetchUpsellProductOffers(value);
    };

    const handleOfferVariantChange = (value: string) => {
        // Atualizar apenas o offerVariantId sem modificar o global
        setFormData(prev => ({
            ...prev,
            offerVariantId: value
        }));
    };

    const handleSwitchChange = (id: string, checked: boolean) => {
        setFormData({ ...formData, [id]: checked });
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name) {
            newErrors.name = "Nome do upsell é obrigatório";
        }

        if (!formData.offerId) {
            newErrors.offerId = "Produto a ser oferecido é obrigatório";
        }

        if (!formData.offerVariantId) {
            newErrors.offerVariantId = "Oferta do produto é obrigatória";
        }

        if (!formData.acceptButtonText) {
            newErrors.acceptButtonText = "Texto do botão de aceite é obrigatório";
        }

        if (!formData.refuseButtonText) {
            newErrors.refuseButtonText = "Texto do botão de recusa é obrigatório";
        }

        if (!formData.acceptRedirectUrl) {
            newErrors.acceptRedirectUrl = "URL de redirecionamento ao aceitar é obrigatória";
        }

        if (!formData.refuseRedirectUrl) {
            newErrors.refuseRedirectUrl = "URL de redirecionamento ao recusar é obrigatória";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }

        // Validar formulário
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setApiError("");
        setApiSuccess("");

        try {
            // Determinar o tipo do upsell com base na seleção da oferta
            const upsellType = formData.global ? 'global' : 'specific';

            // Preparar dados para envio seguindo o formato esperado pela API
            const upsellData: any = {
                productId: productId, // Produto atual que está sendo editado
                productOfferId: formData.offerId, // ID do produto que será oferecido
                offerId: formData.offerVariantId, // ID da oferta específica do produto oferecido
                name: formData.name,
                acceptButtonText: formData.acceptButtonText,
                refuseButtonText: formData.refuseButtonText,
                acceptRedirectUrl: formData.acceptRedirectUrl,
                refuseRedirectUrl: formData.refuseRedirectUrl,
                acceptButtonColor: acceptButtonColor,
                refuseButtonColor: refuseButtonColor,
                type: upsellType,
                settingsData: {
                    active: formData.active,
                    global: formData.global,
                    offerList: selectedOffers,
                    offerVariantId: formData.global ? null : formData.offerVariantId
                },
                // Mantém o script e o triggerId originais
                scriptContent: originalScriptContent,
                triggerId: originalTriggerId,
            };

            // Enviar dados diretamente para a API usando PUT
            const response = await axios.put(
                `${import.meta.env.VITE_API_URL}/api/upsells/${upsellId}`,
                upsellData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            if (response.data.success) {
                setApiSuccess("Upsell atualizado com sucesso!");

                // Chamar o callback de sucesso, se fornecido
                if (onSuccess) {
                    onSuccess(response.data.data);
                }
            } else {
                setApiError(response.data.error || "Erro ao atualizar upsell");

                // Chamar o callback de erro, se fornecido
                if (onError) {
                    onError(response.data.error);
                }
            }
        } catch (error: any) {
            console.error("Erro ao atualizar upsell:", error);
            setApiError(error.response?.data?.error || error.message || "Erro ao atualizar upsell");

            // Chamar o callback de erro, se fornecido
            if (onError) {
                onError(error);
            }
        } finally {
            setLoading(false);
        }
    };

    // Função para gerenciar a seleção de múltiplas ofertas
    const handleOffersChange = (values: string[]) => {
        const isAllSelected = values.includes('all');

        if (isAllSelected) {
            setSelectAllOffers(true);
            setSelectedOffers(['all']);
            setFormData(prev => ({
                ...prev,
                global: true,
                offerList: []
            }));
        } else {
            setSelectAllOffers(false);
            setSelectedOffers(values);
            setFormData(prev => ({
                ...prev,
                global: false,
                offerList: values
            }));
        }
    };

    const handleGoBack = () => {
        navigate(`/products/edit/${productId}?tab=bump`);
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lado esquerdo - Formulário */}
            <div className="col-span-1 lg:col-span-1">
                <ComponentCard title="Editar Upsell">
                    {apiError && (
                        <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4">
                            {apiError}
                        </div>
                    )}

                    {apiSuccess && (
                        <div className="bg-green-50 dark:bg-green-900/10 text-green-600 dark:text-green-400 p-3 rounded-lg mb-4">
                            {apiSuccess}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Switch
                                label="Ativar Upsell"
                                defaultChecked={true}
                                onChange={(checked) => handleSwitchChange("active", checked)}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="name">Nome do Upsell *</Label>
                            <Input
                                type="text"
                                id="name"
                                placeholder="Informe um nome para identificação"
                                onChange={handleInputChange}
                                value={formData.name}
                                error={!!errors.name}
                                hint={errors.name}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="upsellRedirectUrl">Página de upsell *</Label>
                            <Input
                                type="text"
                                id="upsellRedirectUrl"
                                placeholder="Informe a URL da sua página de upsell"
                                onChange={handleInputChange}
                                value={formData.upsellRedirectUrl}
                                error={!!errors.upsellRedirectUrl}
                                hint={errors.upsellRedirectUrl}
                            />
                        </div>

                        <div className="mt-5">
                            <Label>Ao comprar *</Label>
                            {loadingOffers ? (
                                <div className="mt-2 flex items-center text-sm text-gray-500">
                                    <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                    Carregando ofertas...
                                </div>
                            ) : (
                                <div className="mt-2">
                                    <MultiSelectCheckbox
                                        options={[
                                            {
                                                id: 'all',
                                                label: 'Todas as ofertas desse produto',
                                                disabled: false
                                            },
                                            ...productOffers.filter(offer => offer.value !== 'any').map(offer => ({
                                                id: offer.value,
                                                label: `${offer.label}`,
                                                disabled: false,
                                                meta: offer
                                            }))
                                        ]}
                                        selectedValues={selectAllOffers ? ['all'] : selectedOffers}
                                        onChange={handleOffersChange}
                                        placeholder="Selecione as ofertas"
                                        error={!!errors.offers}
                                        hint={errors.offers}
                                        searchPlaceholder="Pesquisar ofertas"
                                        showSelectAll={false}
                                    />
                                    {selectAllOffers &&
                                        <p className="mt-1 text-xs text-blue-500">
                                            Este upsell será válido para todas as ofertas do produto, incluindo as futuras e existentes.
                                        </p>
                                    }
                                </div>
                            )}
                        </div>

                        <div className="mt-5">
                            <Label>Oferecer o produto *</Label>
                            <Select
                                options={products}
                                placeholder={formData.offerProductName || (loadingProducts ? "Carregando produtos..." : "Selecione um produto")}
                                onChange={handleOfferProductChange}
                                value={formData.offerProductName}
                                className={`dark:bg-dark-900 ${loadingProducts ? 'opacity-50 pointer-events-none' : ''} ${errors.offerId ? 'border-red-500' : ''}`}
                            />
                            {errors.offerId && <p className="mt-1 text-sm text-red-500">{errors.offerId}</p>}
                            {loadingProducts && <p className="mt-1 text-sm text-gray-500">Carregando produtos...</p>}
                        </div>

                        <div className="mt-5">
                            <Label>Oferta de Upsell *</Label>
                            <Select
                                options={upsellProductOffers}
                                placeholder={formData.offerName || (loadingUpsellOffers ? "Carregando ofertas..." : "Selecione uma oferta")}
                                onChange={(handleOfferVariantChange)}
                                value={formData.offerVariantId}
                                className={`dark:bg-dark-900 ${!formData.offerId || loadingUpsellOffers ? 'opacity-50 pointer-events-none' : ''} ${errors.offerVariantId ? 'border-red-500' : ''}`}
                            />
                            {errors.offerVariantId && <p className="mt-1 text-sm text-red-500">{errors.offerVariantId}</p>}
                            {loadingUpsellOffers && <p className="mt-1 text-sm text-gray-500">Carregando ofertas...</p>}
                            {!formData.offerId && <p className="mt-1 text-sm text-gray-500">Selecione um produto primeiro</p>}
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="acceptRedirectUrl">Ao aceitar upsell, redirecionar para *</Label>
                            <Input
                                type="text"
                                id="acceptRedirectUrl"
                                placeholder="https://minhapagina.com/agradecimento"
                                onChange={handleInputChange}
                                value={formData.acceptRedirectUrl}
                                error={!!errors.acceptRedirectUrl}
                                hint={errors.acceptRedirectUrl}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="refuseRedirectUrl">Ao recusar upsell, redirecionar para *</Label>
                            <Input
                                type="text"
                                id="refuseRedirectUrl"
                                placeholder="https://minhapagina.com/agradecimento"
                                onChange={handleInputChange}
                                value={formData.refuseRedirectUrl}
                                error={!!errors.refuseRedirectUrl}
                                hint={errors.refuseRedirectUrl}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="acceptButtonText">Texto do botão de aceite *</Label>
                            <Input
                                type="text"
                                id="acceptButtonText"
                                placeholder="Ex: Sim, quero adicionar esse produto ao meu pedido"
                                onChange={handleInputChange}
                                value={formData.acceptButtonText}
                                error={!!errors.acceptButtonText}
                                hint={errors.acceptButtonText}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="refuseButtonText">Texto do botão de recusa *</Label>
                            <Input
                                type="text"
                                id="refuseButtonText"
                                placeholder="Ex: Não, eu não quero essa oferta"
                                onChange={handleInputChange}
                                value={formData.refuseButtonText}
                                error={!!errors.refuseButtonText}
                                hint={errors.refuseButtonText}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                            <ColorPicker
                                color={acceptButtonColor}
                                onChange={setAcceptButtonColor}
                                label="Cor do botão de aceite"
                            />
                            <ColorPicker
                                color={refuseButtonColor}
                                onChange={setRefuseButtonColor}
                                label="Cor do botão de recusa"
                            />
                        </div>

                        <div className="mt-6 flex justify-end gap-4.5">
                            <Button
                                size="md"
                                variant="outline"
                                onClick={handleGoBack}
                            >
                                Cancelar
                            </Button>
                            <Button
                                size="md"
                                variant="primary"
                                onClick={() => handleSubmit()}
                                disabled={loading}
                            >
                                {loading ? "Salvando..." : "Atualizar Upsell"}
                            </Button>
                        </div>
                    </form>
                </ComponentCard>
            </div>

            {/* Lado direito - Preview */}
            <div className="col-span-1">
                <div className="sticky top-4">
                    <UpsellPreview
                        formValues={formData}
                        primaryColor={acceptButtonColor}
                        colorText={refuseButtonColor}
                    />
                </div>
            </div>
        </div>
    );
}